<script setup>
import {computed} from 'vue';
import {useRouter, useRoute} from 'vue-router';
import {frontendTrail} from '~/utils/breadcrumbs';

// X56: the trail is built by utils/breadcrumbs (frontendTrail): Home, then the route's TRAILS entry for the newer
// pages (club booking, memberships & passes, a membership type, tournament stats, live scoring, the profile tabs),
// else the route-name logic this component carried before (legacyTrail, unchanged). A page can pass `items`
// (an explicit trail after Home) or `finalBreadcrumb` (the current page's name) instead. The last crumb is never a link.
const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  club: {
    type: Object,
    default: null
  },
  league: {
    type: Object,
    default: null
  },
  ladder: {
    type: Object,
    default: null
  },
  ladderChallenge: {
    type: Object,
    default: null
  },
  fixture: {
    type: Object,
    default: null
  },
  tournament: {
    type: Object,
    default: null
  },
  meet: {
    type: Object,
    default: null
  },
  // the occurrence (date) when on a meet event page — adds the trailing crumb
  meetOccurrence: {
    type: Object,
    default: null
  },
  americano: {
    type: Object,
    default: null
  },
  // the cycle when viewing a specific americano cycle — adds the trailing crumb
  americanoCycle: {
    type: Object,
    default: null
  },
  howTo: {
    type: Object,
    default: null
  },
  // An explicit trail after Home: [{title, to?}] — the last one is the current page.
  items: {
    type: Array,
    default: null
  },
  // Names the current page when the trail can't (e.g. a membership type's name).
  finalBreadcrumb: {
    type: String,
    default: null
  }
});

const router = useRouter();
const route = useRoute();
// A computed, not a one-shot watch: the club (or whatever names a crumb) can arrive after the header mounts.
const breadcrumbs = computed(() => frontendTrail({
  route,
  routes: router.getRoutes(),
  props,
  items: props.items,
  final: props.finalBreadcrumb,
}));

</script>

<template>
  <!-- redesign breadcrumbs: subtle text links + chevron separators (mockup .breadcrumbs) -->
  <nav class="tl-breadcrumbs" aria-label="Breadcrumb">
    <template v-for="(item, i) in breadcrumbs" :key="i">
      <span v-if="i > 0" class="sep"><v-icon size="14">mdi-chevron-right</v-icon></span>
      <nuxt-link v-if="item.to && !item.disabled" :to="item.to" class="crumb">{{ item.title }}</nuxt-link>
      <span v-else class="crumb here">{{ item.title }}</span>
    </template>
  </nav>
</template>

<style scoped lang="scss">
.tl-breadcrumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 500;
  margin-bottom: 14px;
}

.crumb {
  color: rgb(var(--v-theme-textSubtle));
  text-decoration: none;
  white-space: nowrap;
  /* a long club or competition name is cut, never a sideways scroll */
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 599px) {
  .crumb {
    max-width: 44vw;
  }
}

a.crumb:hover {
  color: rgb(var(--v-theme-charcoal));
}

.crumb.here {
  color: rgb(var(--v-theme-charcoal));
}

.sep {
  display: inline-flex;
  align-items: center;
  color: rgb(var(--v-theme-borderLight));
}
</style>
